/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {ReleaseNotesEditPageComponent} from './release-notes-edit-page.component';
import {collectStatesFromProvider, AdnRouterHelperApi} from '../../routing/state-utils';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {contentPage} from './content-page';
import {SupportingPagesPageComponent} from './supporting-pages-page.component';
import {TcfPageComponent} from './tcf-page.component';
import {AdTransparencyPreviewPageComponent} from './ad-transparency-preview-page.component';
import {PoliticalOverlayPreviewPageComponent} from './political-overlay-preview-page.component';
import {PoliticalPreviewPageComponent} from './political-preview-page.component';
import {ScanResultsPageComponent} from './scan-results-page.component';
import {ElectionsListPageComponent} from './elections-list-page.component';
import {AdDensityPageComponent} from './ad-density-page.component';
import {PreviewShotterPageComponent} from './preview-shotter-page.component';
import {TransparencyShotterPageComponent} from './transparency-shotter-page.component';
import {ShotterPageComponent} from './shotter-page.component';
import {CreativeCheckerPageComponent} from './creative-checker-page.component';
import {ReleaseNotesListPageComponent} from './release-notes-list-page.component';
import {VersionHistoryPageComponent} from './version-history-page.component';
import {VersionCopyPageComponent} from './version-copy-page.component';
import {SupportPageComponent} from './support-page.component';
import {StructurePageComponent} from './structure-page.component';
import {ChainCountsPageComponent} from './chain-counts-page.component';
import {PoliticalSponsorsPageComponent} from './political-sponsors-page.component';
import {TestStylePageComponent} from './test-style-page.component';
import {TfaRecoveryPageComponent} from './tfa-recovery-page.component';
import {ReleaseNotesEditTitleComponent} from './release-notes-edit-title.component';
import {containerStateTitleView} from '../../common/directives/html/route-view-components';


export function simpleStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  return collectStatesFromProvider(function register($stateProvider, adnRouterHelperProvider) {
$stateProvider.state('app.simple', {
          abstract: true
        }).state('app.simple.supporting-pages', {
          url: '/supporting-pages',
          views: {
            'content@app': contentPage(SupportingPagesPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Supporting Pages'
          }
        }).state('app.simple.tcf', {
          url: '/tcf',
          views: {
            'content@app': contentPage(TcfPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'TCF Purposes Checker'
          }
        }).state('app.simple.ad-transparency-preview', {
          url: '/ad-transparency-preview?lineItemId&publisherId',
          views: {
            'content@app': contentPage(AdTransparencyPreviewPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Ad Transparency Page Test Editor'
          }
        }).state('app.simple.political-overlay-preview', {
          url: '/political-overlay-preview',
          views: {
            'content@app': contentPage(PoliticalOverlayPreviewPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Political Overlay Test Editor'
          }
        }).state('nude.political-preview', {
          url: '/political-preview',
          views: {
            'content@nude': contentPage(PoliticalPreviewPageComponent),
            'title@nude': containerStateTitleView()
          },
          data: {
            public: true,
            preview: true,
            title: 'Political Advertising Preview'
          }
        }).state('app.simple.scan-results', {
          url: '/scan-results',
          views: {
            'content@app': contentPage(ScanResultsPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Scan Results'
          }
        }).state('app.simple.elections', {
          url: '/elections',
          views: {
            'content@app': contentPage(ElectionsListPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Elections'
          }
        }).state('app.simple.ad-density', {
          url: '/ad-density',
          views: {
            'content@app': contentPage(AdDensityPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Ad Density'
          }
        }).state('app.simple.preview-shotter', {
          url: '/preview-shotter?creativeId',
          views: {
            'content@app': contentPage(PreviewShotterPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Political Ad Previews'
          }
        }).state('app.simple.transparency-shotter', {
          url: '/transparency-shotter?lineItemId',
          views: {
            'content@app': contentPage(TransparencyShotterPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Political Transparency Screenshots'
          }
        }).state('app.simple.shotter', {
          url: '/shotter?creativeId',
          views: {
            'content@app': contentPage(ShotterPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Screenshotter'
          }
        }).state('app.simple.creative-check', {
          url: '/creative-check?creativeId',
          views: {
            'content@app': contentPage(CreativeCheckerPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Creative Checker'
          }
        }).state('app.simple.release-notes', {
          url: '/release-notes-list',
          views: {
            'content@app': contentPage(ReleaseNotesListPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Admin List of Release Notes'
          }
        }).state('app.simple.release-notes.release-note', {
          url: '/release-note/:id',
          views: {
            'content@app': {
              component: ReleaseNotesEditPageComponent,
              bindings: {isNew: 'isNew'}
            },
            'title@app': {
              component: ReleaseNotesEditTitleComponent
            }
          },
          resolve: {
            isNew: /*@ngInject*/ function($stateParams: any) {
              return $stateParams.id === 'new';
            }
          },
          data: {
            title: 'Edit Release Note'
          }
        }).state('app.simple.version-history', {
          url: '/version-history',
          views: {
            'content@app': contentPage(VersionHistoryPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Version History'
          }
        }).state('app.simple.version-copy', {
          url: '/version-copy',
          views: {
            'content@app': contentPage(VersionCopyPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Version Copy'
          }
        }).state('app.simple.support', {
          url: '/support',
          views: {
            'content@app': contentPage(SupportPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Support Stuff'
          }
        }).state('app.simple.structure', {
          url: '/structure',
          views: {
            'content@app': contentPage(StructurePageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'High-Level Structure'
          }
        }).state('app.simple.chain-counts', {
          url: '/chain-counts',
          views: {
            'content@app': contentPage(ChainCountsPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Chain Counts'
          }
        }).state('app.simple.political-sponsors', {
          url: '/political-sponsors',
          views: {
            'content@app': contentPage(PoliticalSponsorsPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Political Sponsors'
          }
        }).state('app.simple.test-style', {
          url: '/test-style',
          views: {
            'content@app': contentPage(TestStylePageComponent),
            'title@app': stateTitleView()
          },
          data: {
            public: true,
            title: 'Testing the Style of the Custom UI'
          }
        }).state('app.simple.2fa-recovery', {
          url: '/2fa-recovery/:userId',
          views: {
            'content@app': contentPage(TfaRecoveryPageComponent),
            'title@app': stateTitleView()
          },
          data: {
            title: 'Cancel 2FA Requirement'
          }
        });
  }, adnRouterHelper);
}
